<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseInfo from 'dashboard/components-next/AssignmentPolicy/components/BaseInfo.vue';
import DataTable from 'dashboard/components-next/AssignmentPolicy/components/DataTable.vue';
import AddDataDropdown from 'dashboard/components-next/AssignmentPolicy/components/AddDataDropdown.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PolicyFormSection from './PolicyFormSection.vue';
import PolicyFormFooter from './PolicyFormFooter.vue';
import ExclusionRules from 'dashboard/components-next/AssignmentPolicy/components/ExclusionRules.vue';
import InboxCapacityLimits from 'dashboard/components-next/AssignmentPolicy/components/InboxCapacityLimits.vue';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      enabled: false,
      exclusionRules: {
        excludedLabels: [],
        excludeOlderThanHours: null,
      },
      inboxCapacityLimits: [],
    }),
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['CREATE', 'EDIT'].includes(value),
  },
  policyUsers: {
    type: Array,
    default: () => [],
  },
  agentList: {
    type: Array,
    default: () => [],
  },
  labelList: {
    type: Array,
    default: () => [],
  },
  inboxList: {
    type: Array,
    default: () => [],
  },
  showUserSection: {
    type: Boolean,
    default: false,
  },
  showInboxLimitSection: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isUsersLoading: {
    type: Boolean,
    default: false,
  },
  isInboxesLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'submit',
  'addUser',
  'deleteUser',
  'validationChange',
  'deleteInboxLimit',
  'addInboxLimit',
  'updateInboxLimit',
  'cancel',
]);

const { t } = useI18n();

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY';
const REDESIGN_KEY = 'ASSIGNMENT_POLICY.REDESIGN.CAPACITY';
const COMMON_KEY = 'ASSIGNMENT_POLICY.REDESIGN.COMMON';

const baseInfoRef = ref(null);

const state = reactive({
  name: '',
  description: '',
  exclusionRules: {
    excludedLabels: [],
    excludeOlderThanHours: null,
  },
  inboxCapacityLimits: [],
});

const validationState = ref({
  isValid: false,
});

const buttonLabel = computed(() =>
  t(`${BASE_KEY}.${props.mode.toUpperCase()}.${props.mode}_BUTTON`)
);

const footerHint = computed(() =>
  t(
    `${COMMON_KEY}.${validationState.value.isValid ? 'READY_HINT' : 'REQUIRED_HINT'}`
  )
);

const handleValidationChange = validation => {
  validationState.value = validation;
  emit('validationChange', validation);
};

const handleDeleteInboxLimit = id => {
  emit('deleteInboxLimit', id);
};

const handleAddInboxLimit = limit => {
  emit('addInboxLimit', limit);
};

const handleLimitChange = limit => {
  emit('updateInboxLimit', limit);
};

const resetForm = () => {
  Object.assign(state, {
    name: '',
    description: '',
    exclusionRules: {
      excludedLabels: [],
      excludeOlderThanHours: null,
    },
    inboxCapacityLimits: [],
  });
  baseInfoRef.value?.resetValidation();
};

const handleSubmit = () => {
  if (!validationState.value.isValid) {
    baseInfoRef.value?.touchAll();
    return;
  }
  emit('submit', { ...state });
};

watch(
  () => props.initialData,
  newData => {
    Object.assign(state, newData);
  },
  { immediate: true, deep: true }
);

defineExpose({
  resetForm,
});
</script>

<template>
  <form class="flex flex-col gap-4" novalidate @submit.prevent="handleSubmit">
    <div class="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
      <div class="flex min-w-0 flex-col gap-4">
        <PolicyFormSection
          icon="i-hugeicons-file-edit"
          :title="t(`${REDESIGN_KEY}.SECTIONS.IDENTITY.TITLE`)"
          :description="t(`${REDESIGN_KEY}.SECTIONS.IDENTITY.DESCRIPTION`)"
        >
          <BaseInfo
            ref="baseInfoRef"
            v-model:policy-name="state.name"
            v-model:description="state.description"
            :name-label="t(`${BASE_KEY}.FORM.NAME.LABEL`)"
            :name-placeholder="t(`${BASE_KEY}.FORM.NAME.PLACEHOLDER`)"
            :description-label="t(`${BASE_KEY}.FORM.DESCRIPTION.LABEL`)"
            :description-placeholder="
              t(`${BASE_KEY}.FORM.DESCRIPTION.PLACEHOLDER`)
            "
            :name-error-message="t(`${COMMON_KEY}.NAME_REQUIRED`)"
            :description-error-message="t(`${COMMON_KEY}.DESCRIPTION_REQUIRED`)"
            @validation-change="handleValidationChange"
          />
        </PolicyFormSection>

        <PolicyFormSection
          v-if="showInboxLimitSection"
          icon="i-hugeicons-dashboard-speed-02"
          :title="t(`${REDESIGN_KEY}.SECTIONS.LIMITS.TITLE`)"
          :description="t(`${REDESIGN_KEY}.SECTIONS.LIMITS.DESCRIPTION`)"
        >
          <InboxCapacityLimits
            v-model:inbox-capacity-limits="state.inboxCapacityLimits"
            :inbox-list="inboxList"
            :is-fetching="isInboxesLoading"
            @delete="handleDeleteInboxLimit"
            @add="handleAddInboxLimit"
            @update="handleLimitChange"
          />
        </PolicyFormSection>
      </div>

      <div class="flex min-w-0 flex-col gap-4">
        <PolicyFormSection
          icon="i-hugeicons-filter"
          :title="t(`${REDESIGN_KEY}.SECTIONS.EXCLUSIONS.TITLE`)"
          :description="t(`${BASE_KEY}.FORM.EXCLUSION_RULES.DESCRIPTION`)"
        >
          <ExclusionRules
            v-model:excluded-labels="state.exclusionRules.excludedLabels"
            v-model:exclude-older-than-minutes="
              state.exclusionRules.excludeOlderThanHours
            "
            :tags-list="labelList"
          />
        </PolicyFormSection>

        <PolicyFormSection
          v-if="showUserSection"
          icon="i-hugeicons-user-multiple-02"
          :title="t(`${REDESIGN_KEY}.SECTIONS.AGENTS.TITLE`)"
          :description="t(`${REDESIGN_KEY}.SECTIONS.AGENTS.DESCRIPTION`)"
        >
          <template #actions>
            <AddDataDropdown
              :label="t(`${BASE_KEY}.FORM.USERS.ADD_BUTTON`)"
              :search-placeholder="
                t(`${BASE_KEY}.FORM.USERS.DROPDOWN.SEARCH_PLACEHOLDER`)
              "
              :items="agentList"
              @add="$emit('addUser', $event)"
            />
          </template>
          <p class="-mt-1 mb-0 text-xs text-n-slate-11">
            {{ t(`${COMMON_KEY}.IMMEDIATE_SAVE`) }}
          </p>
          <DataTable
            :items="policyUsers"
            :is-fetching="isUsersLoading"
            :navigable="false"
            :empty-state-message="t(`${BASE_KEY}.FORM.USERS.EMPTY_STATE`)"
            @delete="$emit('deleteUser', $event)"
          />
        </PolicyFormSection>

        <aside
          v-if="!showUserSection && !showInboxLimitSection"
          class="rounded-xl border border-dashed border-n-weak p-4 sm:p-5"
        >
          <h2
            class="mb-2 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <Icon icon="i-hugeicons-idea-01" class="size-4 text-n-slate-11" />
            {{ t(`${COMMON_KEY}.NEXT_STEPS`) }}
          </h2>
          <ol class="mb-0 flex list-decimal flex-col gap-1.5 ps-5 text-sm">
            <li class="text-n-slate-11">
              {{ t(`${REDESIGN_KEY}.NEXT_STEP_LIMITS`) }}
            </li>
            <li class="text-n-slate-11">
              {{ t(`${REDESIGN_KEY}.NEXT_STEP_AGENTS`) }}
            </li>
          </ol>
        </aside>
      </div>
    </div>

    <PolicyFormFooter
      :submit-label="buttonLabel"
      :cancel-label="t(`${COMMON_KEY}.CANCEL`)"
      :hint="footerHint"
      :is-valid="validationState.isValid"
      :is-loading="isLoading"
      @cancel="$emit('cancel')"
    />
  </form>
</template>
